import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { DateTime } from "luxon";
import { assetsApiRequest } from "~api/api-request";
import { useRequiredParams } from "~utils/url";
import { ApiResponse, ApiResponsePaginated } from "~api/api-response.model";
import {
  Device,
  DeviceLite,
  DeviceResponse,
  DeviceState,
  DeviceStatusHistory,
} from "./device.model";
import { defaultPageSize, QueryOptions } from "../utils/consts";

type DeviceListParams = {
  levelId?: number;
  companyId: number;
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: string;
  order?: string;
  nested?: string;
  status?: number[];
  deviceIds?: number[];
  externalIds?: string[];
  includeHidden?: boolean;
  version?: string[];
};

type InfiniteDeviceListParams = Omit<DeviceListParams, "page">;

type DeviceStatusHistoryParams = {
  companyId: number;
  deviceId?: number;
  levelId?: number;
  pageSize: number;
  search?: string;
  sort?: string;
  order?: string;
  startTime: Date;
  endTime: Date;
};

type DeviceStateResponse = Omit<DeviceState, "lastOnlineAt"> & {
  lastOnlineAt: string | null;
};

export const deviceQueries = {
  all: (params: DeviceListParams, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["devices", params],
      queryFn: ({ signal }) => fetchDevices(params, signal),
      ...options,
    }),
  statuses: (companyId: number, options?: QueryOptions) =>
    queryOptions({
      queryKey: ["device-statuses", companyId],
      queryFn: ({ signal }) => fetchDeviceStatuses(companyId, signal),
      ...options,
    }),
  detail: (
    params: { companyId: number; id?: number },
    options?: QueryOptions,
  ) =>
    queryOptions({
      queryKey: ["device-detail", params],
      queryFn: ({ signal }) => fetchDevice(params, signal),
      ...options,
    }),
  infinite: (params: InfiniteDeviceListParams) =>
    infiniteQueryOptions({
      queryKey: ["device-list", params],
      gcTime: 0,
      queryFn: ({ signal, pageParam }) =>
        fetchInfiniteDevices({ ...params, pageParam }, signal),
      initialPageParam: 1,
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        // If the length of last page in our fetched pages is less
        // than the page size, we know that there is no more new data to be fetched
        if (
          !lastPage ||
          lastPage.devices.length < (params.pageSize ?? defaultPageSize)
        ) {
          return undefined;
        }
        return lastPageParam + 1;
      },
    }),
  statusHistoryInfinite: (
    params: DeviceStatusHistoryParams,
    options?: QueryOptions,
  ) =>
    infiniteQueryOptions({
      queryKey: ["device-status-history-infinite", params],
      gcTime: 0,
      queryFn: ({ signal, pageParam }) =>
        fetchInfiniteDeviceStatusHistory(
          {
            ...params,
            pageParam,
          },
          signal,
        ),
      initialPageParam:
        params.order === "asc" ? params.startTime : params.endTime,
      getNextPageParam: (lastPage) => {
        // If the length of last page in our fetched pages is less
        // than the page size, we know that there is no more new data to be fetched
        if (!lastPage || lastPage.logs.length < params.pageSize) {
          return undefined;
        }

        return getNextPageTimestamp(lastPage.logs, params.order);
      },
      ...options,
    }),
};

const getNextPageTimestamp = (
  logs: DeviceStatusHistory[],
  order?: string,
): Date | undefined => {
  if (logs.length > 0) {
    const lastLog = logs[logs.length - 1];
    if (lastLog?.timestamp) {
      const isAscending = order === "asc";
      return (
        DateTime.fromISO(lastLog.timestamp)
          // this is to prevent fetching same timestamp entry again for next page
          .plus({ milliseconds: isAscending ? 1 : -1 })
          .toJSDate()
      );
    }
  }
  return undefined;
};

// Queries

export function useDevicesQuery(
  params: Omit<DeviceListParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(
    deviceQueries.all({ companyId: companyId, ...params }, options),
  );
}

export function useDeviceStatusesQuery(options?: QueryOptions) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(deviceQueries.statuses(companyId, options));
}

export function useDeviceQuery(id?: number, options?: QueryOptions) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useQuery(deviceQueries.detail({ companyId, id }, options));
}

export function useDevicesInfiniteQuery(
  params: Omit<InfiniteDeviceListParams, "companyId">,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useInfiniteQuery(deviceQueries.infinite({ companyId, ...params }));
}

export function useDeviceStatusHistoryInfiniteQuery(
  params: Omit<DeviceStatusHistoryParams, "companyId">,
  options?: QueryOptions,
) {
  const { companyId } = useRequiredParams({ companyId: "number" });
  return useInfiniteQuery(
    deviceQueries.statusHistoryInfinite({ companyId, ...params }, options),
  );
}

// Fetchers

async function fetchInfiniteDevices(
  {
    companyId,
    pageParam,
    ...params
  }: InfiniteDeviceListParams & { pageParam: number },
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<
      ApiResponsePaginated<DeviceResponse[]>
    >(`analytics/companies/${companyId}/devices`, { params: { ...params, page: pageParam }, signal })
    .then((resp) => ({
      devices: resp.data.data.map((d) => new Device(d)),
      pagination: resp.data.meta.pagination,
    }));
}

export async function fetchDevices(
  params: DeviceListParams,
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<
      ApiResponsePaginated<DeviceResponse[]>
    >(`analytics/companies/${params.companyId}/devices`, { params, signal })
    .then((resp) => resp.data.data.map((d) => new Device(d)));
}

export async function fetchDeviceStatuses(
  companyId: number,
  signal?: AbortSignal,
) {
  return assetsApiRequest
    .get<
      ApiResponsePaginated<DeviceStateResponse[]>
    >(`analytics/companies/${companyId}/device-statuses`, { signal })
    .then((resp) => resp.data.data.map((d) => new DeviceState(d)));
}

export async function fetchDevice(
  params: { companyId: number; id?: number },
  signal?: AbortSignal,
) {
  if (!params.id) {
    throw new Error("deviceId not defined");
  }
  return assetsApiRequest
    .get<
      ApiResponse<DeviceLite>
    >(`analytics/companies/${params.companyId}/devices/${params.id}`, { signal })
    .then((resp) => new DeviceLite(resp.data.data));
}

export async function fetchDeviceByExternalId(params: {
  companyId: number;
  externalId: string;
  signal?: AbortSignal;
}) {
  const device = await assetsApiRequest
    .get<
      ApiResponse<Device[]>
    >(`analytics/companies/${params.companyId}/devices?externalIds[]=${params.externalId}&pageSize=1&page=1`)
    .then((resp) =>
      resp.data.data.length !== 1
        ? undefined
        : new DeviceLite(resp.data.data[0]),
    );

  return fetchDevice(
    { companyId: params.companyId, id: device?.id },
    params.signal,
  );
}

export async function fetchInfiniteDeviceStatusHistory(
  {
    companyId,
    pageParam,
    ...params
  }: DeviceStatusHistoryParams & { pageParam: Date },
  signal?: AbortSignal,
) {
  // For ascending order: pageParam is the new startTime (moving forward)
  // For descending order: pageParam is the new endTime (moving backward)
  const isAscending = params.order === "asc";
  const startTime = isAscending ? pageParam : params.startTime;
  const endTime = isAscending ? params.endTime : pageParam;

  return assetsApiRequest
    .get<ApiResponsePaginated<DeviceStatusHistory[]>>(
      `analytics/companies/${companyId}/devices/status-history`,
      {
        params: {
          companyId,
          ...params,
          startTime,
          endTime,
          page: 1,
        },
        signal,
      },
    )
    .then((resp) => ({
      logs: resp.data.data.map((item) => new DeviceStatusHistory(item)),
      pagination: resp.data.meta?.pagination,
    }));
}
